/**
 * Save document persistence. ADR-0007: one versioned document in IndexedDB via
 * `idb-keyval`, read once at boot, written back debounced or on demand. This is
 * the only module that touches IndexedDB (TAD-001) — everything else asks it.
 *
 * `KeyValueStore` exists so this file can be tested at all. Tests run in `node`
 * (`vitest.config.ts`), which has no IndexedDB; a version of this file that
 * called `idb-keyval` directly would be exercised by nothing but a browser.
 * `createSaveStore` takes one as a parameter, defaulting to the real thing, the
 * same shape as `loadLevel`'s injected `fetchImpl` in `levels.ts`.
 */

import { get as idbGet, set as idbSet } from 'idb-keyval';

import type { InProgress, SaveDocument } from '@contracts/save';
import { SAVE_SCHEMA_VERSION } from '@contracts/save';
import {
  checkSaveSchemaVersion,
  createDefaultSaveDocument,
  describeFaults,
  migrateSaveDocument,
  validateSaveDocument,
} from '@contracts/save-guards';

export interface KeyValueStore {
  get: (key: string) => Promise<unknown>;
  set: (key: string, value: unknown) => Promise<void>;
}

const idbKeyvalStore: KeyValueStore = { get: idbGet, set: idbSet };

/**
 * `localStorage`'s three synchronous operations, narrowed to what the
 * teardown fallback below needs. Injectable for the same reason
 * `KeyValueStore` is (`node` has no `localStorage` either) -- see
 * `SyncStore`'s own default at the bottom of this file.
 */
export interface SyncStore {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
}

const browserLocalStorage: SyncStore = {
  getItem: (key) => localStorage.getItem(key),
  setItem: (key, value) => {
    localStorage.setItem(key, value);
  },
  removeItem: (key) => {
    localStorage.removeItem(key);
  },
};

/** `PROG-001`: one key for the one document. */
export const SAVE_KEY = 'puzzle-gate:save';

/**
 * `PROG-022`. A save that fails to parse is moved here rather than overwritten,
 * so a player who loses progress to a bug can still get it back if the bug is
 * ever fixed. A second corruption event overwrites whatever this key already
 * held — recovering the *previous* loss is not a promise this makes, only the
 * most recent one.
 */
export const RECOVERY_KEY = 'puzzle-gate:save:unreadable';

/**
 * `PROG-065`, ADR-0012. `save()`'s own write can lose a real race against
 * document teardown -- measured, not assumed, in the resume-round-trip
 * loop's stage 0: a real `pagehide`-triggered reload never once let
 * `store.set()` finish, though the same write finishes in under a
 * millisecond whenever the document survives to let it. `localStorage`'s
 * `setItem` is synchronous, and was the one thing in that same measurement
 * that reliably survived the teardown the IndexedDB write did not.
 *
 * This key holds at most one small record, for at most one page load. It is
 * read and cleared exactly once, at the next boot (`reconcileTeardownFallback`
 * below), whether or not it turns out to be useful -- so a value that never
 * gets reconciled cannot linger to resurrect a level the player has since
 * finished or quit. `SAVE_KEY`'s IndexedDB document stays the one durable,
 * versioned save ADR-0007 requires; this is never a second copy of it, only
 * a relay for the one moment that document's own write cannot be trusted to
 * land in time.
 */
export const TEARDOWN_FALLBACK_KEY = 'puzzle-gate:save:teardown-fallback';

export interface LoadNotice {
  kind: 'unreadable' | 'too-new' | 'timeout';
  detail: string;
}

export interface LoadResult {
  document: SaveDocument;
  /** `false` only for `too-new` (`PROG-024`): the document exists but must not be written to. */
  writable: boolean;
  notice: LoadNotice | null;
}

export interface SaveStore {
  /**
   * `PROG-007`, `PROG-020`: reads the store once and caches the result for the
   * life of this instance. Bounded by `loadTimeoutMs` -- a hang here must not
   * leave `Boot.tsx`'s loading state up forever.
   */
  load: () => Promise<LoadResult>;
  /** Writes immediately; resolves only once the write is durable (`PROG-003`). No-ops if the loaded save was `too-new`. */
  save: (document: SaveDocument) => Promise<void>;
  /** Coalesces writes within the window into one (`PROG-009`). */
  scheduleSave: (document: SaveDocument) => void;
  /** Writes a pending scheduled save immediately, e.g. before the session ends (`PROG-004`). No-op if nothing is pending. */
  flush: () => Promise<void>;
  /**
   * `PROG-065`, ADR-0012. Synchronous, unlike everything else here on
   * purpose -- call this from the same teardown handler that calls `save`
   * or `scheduleSave`, not instead of it. `reconcileTeardownFallback`
   * (internal to this module, exercised through `load`) is what actually
   * makes the value useful, at the next boot.
   */
  persistInProgressSync: (inProgress: InProgress) => void;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Default coalescing window. `PROG-009` names 500 ms. */
const DEFAULT_COALESCE_MS = 500;

/**
 * How long boot waits on `store.get` before giving up on it. No requirement
 * names this figure -- TAD-001 records the reasoning.
 */
const DEFAULT_LOAD_TIMEOUT_MS = 3000;

/** Distinguishes "the store answered `undefined`" from "the store never answered". */
const TIMED_OUT = Symbol('save load timed out');

function timeout(ms: number): Promise<typeof TIMED_OUT> {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(TIMED_OUT);
    }, ms);
  });
}

/**
 * `record.inProgress`, checked as narrowly as `deserialiseBoard`'s own
 * callers check theirs -- enough to trust `gameId`/`levelId`/`savedAt`, not
 * a claim about `state`, which is opaque to this layer the same way it is
 * to `storage.ts`'s only other reader of it, `SaveDocument` itself.
 */
function isInProgressShaped(value: unknown): value is InProgress {
  if (!isRecord(value)) return false;
  return (
    typeof value.gameId === 'string' &&
    typeof value.levelId === 'string' &&
    typeof value.savedAt === 'string'
  );
}

export function createSaveStore(
  store: KeyValueStore = idbKeyvalStore,
  coalesceMs = DEFAULT_COALESCE_MS,
  now: () => string = () => new Date().toISOString(),
  loadTimeoutMs = DEFAULT_LOAD_TIMEOUT_MS,
  syncStore: SyncStore = browserLocalStorage,
): SaveStore {
  let cachedLoad: Promise<LoadResult> | undefined;
  let writable = true;
  let pendingDoc: SaveDocument | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  function persistInProgressSync(inProgress: InProgress): void {
    try {
      syncStore.setItem(TEARDOWN_FALLBACK_KEY, JSON.stringify(inProgress));
    } catch {
      // Storage unavailable or full -- the caller also calls the ordinary
      // async `save`/`scheduleSave` regardless, so this is not the only
      // attempt at durability, only the one measured to survive teardown.
    }
  }

  /**
   * `ADR-0012`. Read and cleared exactly once, every boot, whether or not
   * it turns out to be useful.
   *
   * `documentUpdatedAtIsTrustworthy` matters and is not a simplification --
   * found only by re-measuring this stage's own fix rather than trusting the
   * unit tests alone (the self-review gate's "did you look at it", applied
   * to a non-visual claim: did the real thing happen, not just the mocked
   * one). `createDefaultSaveDocument`'s `updatedAt` is stamped `now()` at
   * the moment it is synthesised -- for `PROG-021` (nothing saved yet) and
   * every `recoverUnreadable`/timeout/too-new path, that "now" carries no
   * information about anything durable, but it always lexically outsorts a
   * fallback captured a few seconds earlier in the *previous* session. Compared
   * unconditionally, that discarded the fallback as "stale" on every boot
   * with no prior real document -- exactly the first-ever-level case this
   * fix most needs to cover. Only a document actually read out of the store
   * (`case 'ok'`, or `too-old` successfully migrated) has an `updatedAt`
   * worth comparing against; every synthesised default applies the fallback
   * unconditionally instead; there is no real competing claim to protect.
   */
  function reconcileTeardownFallback(
    document: SaveDocument,
    documentUpdatedAtIsTrustworthy: boolean,
  ): SaveDocument {
    let raw: string | null;
    try {
      raw = syncStore.getItem(TEARDOWN_FALLBACK_KEY);
    } catch {
      return document;
    }
    if (raw === null) return document;

    try {
      syncStore.removeItem(TEARDOWN_FALLBACK_KEY);
    } catch {
      // Best-effort clear. Falling through with a value already read is
      // still correct either way.
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return document;
    }
    if (!isInProgressShaped(parsed)) return document;
    if (documentUpdatedAtIsTrustworthy && parsed.savedAt <= document.updatedAt) return document;

    return { ...document, inProgress: parsed };
  }

  /**
   * `real: false` on every branch that fabricates a document rather than
   * reading one back -- `reconcileTeardownFallback`'s own comment has the
   * reasoning for why that distinction has to survive past `readFromStore`.
   */
  async function recoverUnreadable(
    raw: unknown,
    detail: string,
  ): Promise<LoadResult & { real: boolean }> {
    try {
      await store.set(RECOVERY_KEY, raw);
    } catch {
      // The store itself has refused a write; PROG-025's retention promise is
      // best-effort once that happens, since there is nowhere left to put the
      // bytes.
    }
    return {
      document: createDefaultSaveDocument(now),
      writable: true,
      notice: { kind: 'unreadable', detail },
      real: false,
    };
  }

  async function readFromStore(): Promise<LoadResult & { real: boolean }> {
    let raw: unknown;
    try {
      const result = await Promise.race([store.get(SAVE_KEY), timeout(loadTimeoutMs)]);
      if (result === TIMED_OUT) {
        // The abandoned `store.get` is left to resolve or never resolve on its
        // own; nothing is still listening. `writable: false`, the same guard
        // `too-new` uses, matters more here than there: the read that timed
        // out might still land seconds later with the *real* document, and
        // writing a fresh default over it the moment the player does anything
        // would be exactly the silent discard `PROG-025` forbids. A slow read
        // costs this session's progress; it must not cost the previous one's.
        return {
          document: createDefaultSaveDocument(now),
          writable: false,
          notice: {
            kind: 'timeout',
            detail: `save did not load within ${String(loadTimeoutMs)}ms`,
          },
          real: false,
        };
      }
      raw = result;
    } catch {
      // Storage is unavailable (`PROG-027`, manual verification, not this
      // stage). There are no bytes to retain because none arrived, so this is
      // the one unreadable case with nothing to hand `recoverUnreadable`.
      return {
        document: createDefaultSaveDocument(now),
        writable: true,
        notice: null,
        real: false,
      };
    }

    if (raw === undefined) {
      // `PROG-021`.
      return {
        document: createDefaultSaveDocument(now),
        writable: true,
        notice: null,
        real: false,
      };
    }

    if (!isRecord(raw)) {
      return recoverUnreadable(raw, `save is ${typeof raw}, not a document`);
    }

    switch (checkSaveSchemaVersion(raw.schemaVersion)) {
      case 'too-new':
        // `PROG-024`. This build does not know this document's shape and must
        // not guess at it, so play proceeds on an in-memory default rather than
        // an interpretation of data it cannot understand — `writable: false` is
        // what stops that default from ever being written back over the real
        // save.
        return {
          document: createDefaultSaveDocument(now),
          writable: false,
          notice: {
            kind: 'too-new',
            detail: `save was written by a newer version (schema ${String(raw.schemaVersion)})`,
          },
          real: false,
        };

      case 'too-old': {
        // `PROG-023`. `raw.schemaVersion` is a valid integer here, checked by
        // `checkSaveSchemaVersion` immediately above.
        const migrated = migrateSaveDocument(raw, raw.schemaVersion as number);
        if (!migrated.ok) {
          return recoverUnreadable(
            raw,
            `schema ${String(raw.schemaVersion)} has no migration path to ${String(SAVE_SCHEMA_VERSION)}`,
          );
        }
        const validated = validateSaveDocument(migrated.value);
        if (!validated.ok) {
          return recoverUnreadable(
            raw,
            `migrated save is invalid: ${describeFaults(validated.faults)}`,
          );
        }
        // Not persisted here. Reading is not the moment to write; the next
        // `save()` or `scheduleSave()` call carries the migrated shape forward.
        return { document: validated.value, writable: true, notice: null, real: true };
      }

      case 'unreadable':
        return recoverUnreadable(raw, 'save does not declare a readable schema version');

      case 'ok': {
        const validated = validateSaveDocument(raw);
        if (!validated.ok) {
          return recoverUnreadable(raw, describeFaults(validated.faults));
        }
        return { document: validated.value, writable: true, notice: null, real: true };
      }
    }
  }

  function load(): Promise<LoadResult> {
    cachedLoad ??= readFromStore().then(({ real, ...result }) => {
      writable = result.writable;
      return { ...result, document: reconcileTeardownFallback(result.document, real) };
    });
    return cachedLoad;
  }

  async function save(document: SaveDocument): Promise<void> {
    // A pending coalesced write is superseded by this one either way: if this
    // write proceeds it carries newer data, and if it does not (not writable)
    // the pending write must not fire later and overwrite the same document.
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
      pendingDoc = null;
    }

    await load();
    if (!writable) return;

    await store.set(SAVE_KEY, { ...document, updatedAt: now() });
  }

  function scheduleSave(document: SaveDocument): void {
    pendingDoc = document;
    // A window is already open; it will pick up this document when it fires.
    // Resetting the timer on every call would let writes keep arriving forever
    // without ever coalescing into one -- the same throttle-not-debounce choice
    // `platform/announce.ts` makes for polite announcements, and for the same
    // reason.
    if (timer !== null) return;
    timer = setTimeout(() => {
      timer = null;
      const toWrite = pendingDoc;
      pendingDoc = null;
      if (toWrite) void save(toWrite);
    }, coalesceMs);
  }

  async function flush(): Promise<void> {
    if (timer === null) return;
    clearTimeout(timer);
    timer = null;
    const toWrite = pendingDoc;
    pendingDoc = null;
    if (toWrite) await save(toWrite);
  }

  return { load, save, scheduleSave, flush, persistInProgressSync };
}

/** The application's one save store. Everything outside this file asks this. */
export const saveStore: SaveStore = createSaveStore();
